import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  Container, Grid, Paper, Typography, Box, Tabs, Tab
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import TaskIcon from '@mui/icons-material/Task';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import {
  PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid
} from 'recharts';

const API = process.env.REACT_APP_API_URL;

const COLORES = ['#1F4E79', '#2E75B6', '#70AD47', '#ED7D31', '#FFC000', '#FF0000'];

const TIPO_LABELS = {
  billable: 'Billable',
  non_billable: 'Non-Billable',
  descanso: 'Descanso',
  vacaciones: 'Vacaciones',
  permisos: 'Permisos'
};

function StatCard({ title, value, icon, color }) {
  return (
    <Paper elevation={3} sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ color, fontSize: 40 }}>{icon}</Box>
        <Box>
          <Typography variant="h4" fontWeight="bold">{value}</Typography>
          <Typography variant="body2" color="text.secondary">{title}</Typography>
        </Box>
      </Box>
    </Paper>
  );
}

function getWeekDates() {
  const hoy = new Date();
  const day = hoy.getDay();
  const diff = hoy.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(hoy.setDate(diff));
  const days = [];
  for (let i = 0; i < 7; i++) {
    const next = new Date(monday);
    next.setDate(monday.getDate() + i);
    days.push(next);
  }
  return days;
}

function getMonthDates() {
  const hoy = new Date();
  const primerDia = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
  const ultimoDia = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0);
  return { primerDia, ultimoDia };
}

function formatDate(date) {
  return date.toISOString().split('T')[0];
}

export default function Dashboard() {
  const { usuario } = useAuth();
  const [proyectos, setProyectos] = useState([]);
  const [tareas, setTareas] = useState([]);
  const [registrosSemana, setRegistrosSemana] = useState([]);
  const [registrosMes, setRegistrosMes] = useState([]);
  const [tabActivo, setTabActivo] = useState(0);

  useEffect(() => {
    // Cargar proyectos y tareas
    axios.get(`${API}/api/proyectos`, { params: { empresa_id: usuario?.empresa_id } })
      .then(r => setProyectos(Array.isArray(r.data) ? r.data : []))
      .catch(() => setProyectos([]));

    axios.get(`${API}/api/tareas`, { params: { empresa_id: usuario?.empresa_id } })
      .then(r => setTareas(Array.isArray(r.data) ? r.data : []))
      .catch(() => setTareas([]));

    // Cargar registros de la semana actual
    const weekDays = getWeekDates();
    const fechaInicioSemana = formatDate(weekDays[0]);
    const fechaFinSemana = formatDate(weekDays[6]);
    axios.get(`${API}/api/tiempos/usuario/${usuario.id}?fecha_inicio=${fechaInicioSemana}&fecha_fin=${fechaFinSemana}`)
      .then(r => setRegistrosSemana(Array.isArray(r.data) ? r.data : []))
      .catch(() => setRegistrosSemana([]));

    // Cargar registros del mes actual
    const { primerDia, ultimoDia } = getMonthDates();
    axios.get(`${API}/api/tiempos/usuario/${usuario.id}?fecha_inicio=${formatDate(primerDia)}&fecha_fin=${formatDate(ultimoDia)}`)
      .then(r => setRegistrosMes(Array.isArray(r.data) ? r.data : []))
      .catch(() => setRegistrosMes([]));
  }, []);

  const completadas = tareas.filter(t => t.estado === 'completada').length;
  const pendientes = tareas.filter(t => t.estado === 'pendiente').length;

  // Datos para torta — distribución por tipo
  const getDatosTorta = (registros) => {
    const totales = {};
    registros.forEach(r => {
      totales[r.tipo] = (totales[r.tipo] || 0) + r.horas;
    });
    return Object.entries(totales).map(([tipo, horas]) => ({
      name: TIPO_LABELS[tipo] || tipo,
      value: horas
    }));
  };

  // Datos para barra — horas por día
  const getDatosBarra = (registros) => {
    const DIAS = ['Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sa', 'Do'];
    const weekDays = getWeekDates();
    return weekDays.map((day, i) => {
      const dayStr = formatDate(day);
      const horasDia = registros
        .filter(r => r.fecha?.split('T')[0] === dayStr)
        .reduce((sum, r) => sum + r.horas, 0);

      // Desglose por tipo
      const desglose = {};
      registros
        .filter(r => r.fecha?.split('T')[0] === dayStr)
        .forEach(r => {
          desglose[r.tipo] = (desglose[r.tipo] || 0) + r.horas;
        });

      return {
        dia: DIAS[i],
        total: horasDia,
        ...desglose
      };
    });
  };

  // Top proyecto
  const getTopProyecto = (registros) => {
    const totales = {};
    registros.forEach(r => {
      if (r.proyecto_id) {
        totales[r.proyecto_id] = (totales[r.proyecto_id] || 0) + r.horas;
      }
    });
    const topId = Object.entries(totales).sort((a, b) => b[1] - a[1])[0]?.[0];
    const proyecto = proyectos.find(p => p.id === parseInt(topId));
    return proyecto?.nombre || '—';
  };

  const registrosActivos = tabActivo === 0 ? registrosSemana : registrosMes;
  const totalHoras = registrosActivos.reduce((sum, r) => sum + r.horas, 0);
  const datosTorta = getDatosTorta(registrosActivos);
  const datosBarra = getDatosBarra(registrosSemana);
  const topProyecto = getTopProyecto(registrosActivos);

  return (
    <Container sx={{ mt: 4 }}>
      <Typography variant="h4" gutterBottom>
        Bienvenido, {usuario?.nombre} 👋
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Resumen general de tu equipo
      </Typography>

      {/* Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Proyectos"
            value={proyectos.length}
            icon={<FolderIcon fontSize="inherit" />}
            color="primary.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Tareas totales"
            value={tareas.length}
            icon={<TaskIcon fontSize="inherit" />}
            color="info.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Completadas"
            value={completadas}
            icon={<CheckCircleIcon fontSize="inherit" />}
            color="success.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Pendientes"
            value={pendientes}
            icon={<PendingIcon fontSize="inherit" />}
            color="warning.main"
          />
        </Grid>
      </Grid>

      {/* Tabs semana/mes */}
      <Paper elevation={3} sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Distribución de Tiempos</Typography>
          <Tabs value={tabActivo} onChange={(e, v) => setTabActivo(v)} size="small">
            <Tab label="Esta semana" />
            <Tab label="Este mes" />
          </Tabs>
        </Box>

        {/* KPIs rápidos */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#F0F4F8' }}>
              <Typography variant="h4" fontWeight="bold" color="primary">
                {totalHoras}h
              </Typography>
              <Typography variant="body2" color="text.secondary">Total horas</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#F0F4F8' }}>
              <Typography variant="h5" fontWeight="bold" color="success.main">
                {topProyecto}
              </Typography>
              <Typography variant="body2" color="text.secondary">Top Proyecto</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#F0F4F8' }}>
              <Typography variant="h4" fontWeight="bold" color="info.main">
                {registrosActivos.filter(r => r.tipo === 'billable').reduce((sum, r) => sum + r.horas, 0)}h
              </Typography>
              <Typography variant="body2" color="text.secondary">Horas Billable</Typography>
            </Paper>
          </Grid>
        </Grid>

        <Box container spacing={3}>
          {/* Barra — fila completa */}
          <Box sx={{ width: '100%', mb: 4 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Horas por día (semana actual)
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={datosBarra} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="dia" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="billable" name="Billable" stackId="a" fill="#1F4E79" />
                <Bar dataKey="non_billable" name="Non-Billable" stackId="a" fill="#ED7D31" />
                <Bar dataKey="descanso" name="Descanso" stackId="a" fill="#70AD47" />
                <Bar dataKey="vacaciones" name="Vacaciones" stackId="a" fill="#FFC000" />
                <Bar dataKey="permisos" name="Permisos" stackId="a" fill="#9B59B6" />
              </BarChart>
            </ResponsiveContainer>
          </Box>

          {/* Torta */}
          <Box sx={{ width: '100%' }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Distribución por tipo ({tabActivo === 0 ? 'semana' : 'mes'})
            </Typography>
            {datosTorta.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={datosTorta}
                    cx="50%"
                    cy="50%"
                    innerRadius={80}
                    outerRadius={120}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {datosTorta.map((entry, index) => (
                      <Cell key={index} fill={COLORES[index % COLORES.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => `${value}h`} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 250 }}>
                <Typography color="text.secondary">No hay registros de tiempo</Typography>
              </Box>
            )}
          </Box>
        </Box>  
      </Paper>
    </Container>
  );
}